import React from 'react';
import { Link } from 'react-router-dom';

import { ArrowsPointingInIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import { PageTitle } from '@oysterjs/ui/page';
import { Spinner } from '@oysterjs/ui/spinner';

import {
  ClientMergeCandidateReason,
  ClientMergeCandidatesQuery,
  MergeCandidateSideFragment,
  useClientMergeCandidatesQuery,
  useKeepClientsSeparateMutation
} from '~/api/graphql';

import { ClientMergeDialog, ClientMergeGroup } from '~/components/clients/client-merge-dialog';
import { ConnectionLoadMoreFooter } from '~/components/connection-load-more-footer';
import { FormError } from '~/components/form-error';
import { ClientTabEmptyState } from '~/components/clients/client-panels';
import { ClientLinesBadges, countLine, OrDash } from '~/components/clients/clients-columns';
import { dateFormatter } from '~/components/table-formatters';

const DUPLICATES_PAGE_SIZE = 25;

type Candidate = NonNullable<
  ClientMergeCandidatesQuery['clientMergeCandidates']['edges'][number]
>['node'];

/**
 * The queue is empty for most agencies -- 44% of the book at last measure -- so
 * this is the common rendering rather than a fallback.
 */
export const NothingToReview = () => (
  <ClientTabEmptyState
    icon={CheckIcon}
    title="No duplicates to review"
    description="Pairs appear here when two clients look alike enough that only you can say whether they are the same."
  />
);

/**
 * The counts are the whole point of the comparison: they tell a client with a
 * history from one created by a stray spelling.
 */
const CandidateSide = (props: { client: MergeCandidateSideFragment }) => (
  <li className="space-y-1 px-4 py-3">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <Link
        to={`/app/clients/${props.client.id}`}
        className="text-sm font-semibold text-neutral-900 hover:underline dark:text-neutral-100"
      >
        {props.client.name}
      </Link>
      <ClientLinesBadges
        isCommercial={props.client.isCommercial}
        isPersonal={props.client.isPersonal}
      />
    </div>
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-neutral-500 dark:text-neutral-400">
      <OrDash value={props.client.email} />
      <OrDash value={props.client.phone} />
    </div>
    <p className="text-sm text-neutral-500 dark:text-neutral-400">
      {countLine(props.client)} · Added {dateFormatter.format(new Date(props.client.createdAt))}
    </p>
  </li>
);

/** The score only grades name matches; every other reason reports 1. */
const matchLabel = (candidate: Candidate) => {
  switch (candidate.reason) {
    case ClientMergeCandidateReason.NameAmbiguous:
      return `Names ${Math.round(candidate.score * 100)}% alike`;
    case ClientMergeCandidateReason.AmsLinkAmbiguous:
      return 'Same AMS account';
    case ClientMergeCandidateReason.CrossIntegration:
      return 'In two AMSes';
    case ClientMergeCandidateReason.UserSelected:
      return 'Picked by hand';
  }
};

// Memoized, or opening the page's merge dialog re-renders every loaded card.
const CandidateCard = React.memo(function CandidateCard(props: {
  candidate: Candidate;
  onMerge: (candidate: Candidate) => void;
  onDismiss: (candidateId: string) => void;
}) {
  const { candidate } = props;
  const [error, setError] = React.useState<string | null>(null);
  const [keepSeparate, { loading: keeping }] = useKeepClientsSeparateMutation();

  const confirmSeparate = async () => {
    setError(null);
    const result = await keepSeparate({ variables: { candidateId: candidate.id } });
    if (result.errors?.length) {
      setError('That did not go through. Reload and try again.');
      return;
    }
    props.onDismiss(candidate.id);
  };

  return (
    <div className="space-y-3 rounded-lg bg-white p-4 shadow sm:px-6 dark:bg-neutral-800">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
          Possibly the same client
        </h3>
        <Badge color="zinc">{matchLabel(candidate)}</Badge>
      </div>

      <ul className="divide-y divide-neutral-200 rounded-lg border border-neutral-200 dark:divide-neutral-700 dark:border-neutral-700">
        <CandidateSide client={candidate.clientA} />
        <CandidateSide client={candidate.clientB} />
      </ul>

      <FormError message={error} />

      <div className="flex flex-wrap justify-end gap-2">
        <Button plain disabled={keeping} onClick={() => props.onDismiss(candidate.id)}>
          Skip for now
        </Button>
        <Button outline disabled={keeping} onClick={confirmSeparate}>
          {keeping ? 'Saving...' : 'Not the same client'}
        </Button>
        <Button color="sky" disabled={keeping} onClick={() => props.onMerge(candidate)}>
          <ArrowsPointingInIcon data-slot="icon" />
          Merge clients
        </Button>
      </div>
    </div>
  );
});

export const ClientDuplicatesPage = () => {
  // No refetch on a skip or keep-apart: it would renumber the list mid-decision.
  // The merge dialog refetches after a merge.
  const [dismissed, setDismissed] = React.useState<ReadonlySet<string>>(new Set());
  const dismiss = React.useCallback(
    (id: string) => setDismissed((prev) => new Set(prev).add(id)),
    []
  );

  const [mergeGroup, setMergeGroup] = React.useState<ClientMergeGroup | null>(null);

  const { data, loading, fetchMore } = useClientMergeCandidatesQuery({
    variables: { first: DUPLICATES_PAGE_SIZE },
    notifyOnNetworkStatusChange: true
  });

  const connection = data?.clientMergeCandidates;
  const candidates = React.useMemo(
    () => (connection?.edges ?? []).map((edge) => edge.node).filter((c) => !dismissed.has(c.id)),
    [connection?.edges, dismissed]
  );
  const hasNextPage = connection?.pageInfo.hasNextPage ?? false;
  const endCursor = connection?.pageInfo.endCursor ?? null;

  const startMerge = React.useCallback(
    (candidate: Candidate) =>
      setMergeGroup({ clientIds: [candidate.clientA.id, candidate.clientB.id] }),
    []
  );

  const loadMore = React.useCallback(() => {
    if (!endCursor || !hasNextPage) return;
    fetchMore({ variables: { after: endCursor, first: DUPLICATES_PAGE_SIZE } });
  }, [endCursor, hasNextPage, fetchMore]);

  return (
    <PageTitle
      eyebrow="Clients"
      eyebrowHref="/app/clients"
      title="Possible duplicates"
      description="Pairs of clients that may be the same. Only you can say."
    >
      {loading && !connection ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : candidates.length === 0 ? (
        <NothingToReview />
      ) : (
        <div className="space-y-4">
          {candidates.map((candidate) => (
            <CandidateCard
              key={candidate.id}
              candidate={candidate}
              onMerge={startMerge}
              onDismiss={dismiss}
            />
          ))}
          <ConnectionLoadMoreFooter
            shown={candidates.length}
            totalCount={connection?.totalCount}
            noun="pairs"
            hasNextPage={hasNextPage}
            onLoadMore={loadMore}
            loading={loading}
          />
        </div>
      )}

      <ClientMergeDialog
        group={mergeGroup}
        onClose={() => setMergeGroup(null)}
        onMerged={() => setMergeGroup(null)}
      />
    </PageTitle>
  );
};
